import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { architecture } from "../../content/siteContent";
import { SectionHeading } from "./primitives";
import { Reveal } from "../motion/Reveal";
import { track, EVENTS } from "../../lib/analytics";

const ModuleRow = ({ module: m, isOpen, onToggle, testid }) => (
  <div className="border border-hair rounded-sm bg-white transition-colors duration-300 hover:border-teal/60">
    <button
      data-testid={testid}
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-label={`${m.code} — ${m.title}`}
      className="w-full flex items-center justify-between gap-4 px-5 py-4 lg:px-6 lg:py-5 text-left focus-ring rounded-sm"
    >
      <div className="flex items-center gap-4 min-w-0">
        <span className="font-display text-xs font-bold text-teal shrink-0 w-9">{m.code}</span>
        <span className="font-display font-semibold text-navy text-base lg:text-[17px] tracking-tight truncate">
          {m.title}
        </span>
      </div>
      <span
        className={`flex h-7 w-7 items-center justify-center rounded-sm border shrink-0 transition-colors duration-300 ${
          isOpen ? "bg-teal border-teal text-white" : "border-hair text-slate-ink"
        }`}
      >
        {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <div className="px-5 lg:px-6 pb-5 lg:pb-6 pl-[52px] lg:pl-[60px]">
            <p className="text-slate-ink text-sm lg:text-[15px] leading-relaxed">{m.desc}</p>
            {m.output && (
              <p className="mt-3 flex flex-wrap items-baseline gap-x-2 text-xs">
                <span className="eyebrow text-teal">Key outputs</span>
                <span className="text-navy">{m.output}</span>
              </p>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const PhaseBlock = ({ phase: p, index, isLast }) => {
  const [open, setOpen] = useState(0);
  const facets = [
    { label: "Capability", value: p.capability },
    { label: "In the workplace", value: p.workplace },
    { label: "Outputs", value: p.outputs },
  ];
  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start ${
        index > 0 ? "mt-16 lg:mt-24 pt-16 lg:pt-24 border-t border-hair" : "mt-14 lg:mt-20"
      }`}
    >
      {/* Left — sticky phase info */}
      <div
        data-testid={`phase-block-${p.number}`}
        className="lg:col-span-5 lg:sticky lg:top-28 flex flex-col"
      >
        <div className="flex items-baseline gap-4">
          <span className="font-display font-extrabold text-gold text-5xl lg:text-6xl leading-none tracking-tight">
            {p.number}
          </span>
          <span className="eyebrow text-slate-ink">{p.moduleRange}</span>
        </div>
        <h3 className="mt-5 font-display font-extrabold text-navy text-2xl lg:text-3xl tracking-tight leading-tight">
          {p.title}
        </h3>
        <p className="mt-3 text-slate-ink text-sm lg:text-base leading-relaxed max-w-md">{p.idea}</p>

        <div className="mt-8 space-y-5">
          {facets.map((f) => (
            <div key={f.label} className="border-l-2 border-gold pl-4">
              <p className="eyebrow text-teal">{f.label}</p>
              <p className="mt-1.5 text-navy text-sm leading-relaxed">{f.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right — modules for this phase */}
      <div className="lg:col-span-7">
        <p className="eyebrow text-slate-ink mb-4">
          What you'll study · {p.modules.length} modules
        </p>
        <div className="flex flex-col gap-3">
          {p.modules.map((m, i) => (
            <ModuleRow
              key={m.title}
              module={m}
              isOpen={open === i}
              testid={`phase-${p.number}-module-${i}-accordion`}
              onToggle={() => {
                const next = open === i ? -1 : i;
                setOpen(next);
                if (next === i) track(EVENTS.MODULE_EXPLORER, { phase: p.title, module: m.title });
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const Architecture = () => {
  return (
    <section id="system" data-testid="architecture-section" className="bg-offwhite py-24 lg:py-32 border-t border-hair">
      <div className="container-page">
        <SectionHeading eyebrow={architecture.eyebrow}>{architecture.heading}</SectionHeading>
        <Reveal delay={0.1}>
          <p className="mt-6 text-slate-ink text-base lg:text-lg leading-relaxed max-w-2xl">
            {architecture.intro}
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-wrap items-center gap-x-2 gap-y-3" data-testid="learning-loop">
            <span className="eyebrow text-teal mr-2">The applied-learning loop</span>
            {architecture.learningLoop.map((step, i) => (
              <React.Fragment key={step}>
                <span className="inline-flex items-center rounded-sm border border-hair px-3 py-1.5 font-display font-semibold text-navy text-xs">
                  {step}
                </span>
                {i < architecture.learningLoop.length - 1 && <span className="text-teal">→</span>}
              </React.Fragment>
            ))}
          </div>
        </Reveal>

        {architecture.phases.map((p, i) => (
          <PhaseBlock
            key={p.number}
            phase={p}
            index={i}
            isLast={i === architecture.phases.length - 1}
          />
        ))}
      </div>
    </section>
  );
};
